import {
  generateArraySchema,
  generateBooleanSchema,
  generateColorSchema,
  generateObjectSchema,
  generateRootObjectSchema,
  generateStringSchema,
} from "components/cards/common/schema";
import { BaseAtomSpec, generateAtomContainerSchema } from "components/cards/atoms/BaseAtomSpec";
import { TextParticleSpec } from "components/cards/particles/YinzCam";

const partnershipSchema = generateObjectSchema(
  "Partnership",
  "A single normalized partnership row.",
  {
    wicket: generateStringSchema("Wicket", "The wicket number for the partnership."),
    runs: generateStringSchema("Runs", "Total runs scored in the partnership."),
    balls: generateStringSchema("Balls", "Total balls faced in the partnership."),
    dots: generateStringSchema("Dots", "Total dot balls in the partnership."),
    batter1Id: generateStringSchema("Batter 1 Id", "Player id for batter 1."),
    batter1Name: generateStringSchema("Batter 1 Name", "Display name for batter 1."),
    batter1Runs: generateStringSchema("Batter 1 Runs", "Runs scored by batter 1."),
    batter1Balls: generateStringSchema("Batter 1 Balls", "Balls faced by batter 1."),
    batter2Id: generateStringSchema("Batter 2 Id", "Player id for batter 2."),
    batter2Name: generateStringSchema("Batter 2 Name", "Display name for batter 2."),
    batter2Runs: generateStringSchema("Batter 2 Runs", "Runs scored by batter 2."),
    batter2Balls: generateStringSchema("Batter 2 Balls", "Balls faced by batter 2."),
  },
  { additionalProperties: true }
);

const inningsSchema = generateObjectSchema(
  "Innings",
  "Normalized partnership rows grouped by innings.",
  {
    number: generateStringSchema("Number", "Innings number."),
    teamTriCode: generateStringSchema("Team Tricode", "Batting team tricode for this innings."),
    teamInningsNumber: generateStringSchema("Team Innings Number", "Nth innings for this team (1, 2, etc)."),
    tabLabel: generateStringSchema("Tab Label", "Display label for innings tab (for example, \"SUR 1ST\")."),
    partnerships: generateArraySchema(
      "Partnership Rows",
      "Normalized partnerships for this innings.",
      partnershipSchema
    ),
  },
  { additionalProperties: true }
);

export const specification = new BaseAtomSpec(
  "Partnership Graph Card",
  generateRootObjectSchema({
    container: generateAtomContainerSchema({
      defaultValues: { themeMode: "primary" },
    }),
    overlayPadding: generateStringSchema("Overlay Padding (Desktop)", "Inner padding for desktop layout.", {
      defaultValue: "1rem 1.5rem",
    }),
    overlayPaddingMobile: generateStringSchema("Overlay Padding (Mobile)", "Inner padding for mobile layout.", {
      defaultValue: "1rem 0.75rem",
    }),
    legendBatter1Label: TextParticleSpec.generateSchema("Legend Label 1", "Legend text for batter 1.", {
      defaultValues: {
        text: "Batter 1",
        family: "Inter, sans-serif",
        size: "0.6875rem",
        weight: "400",
        color: "var(--theme-mode-foreground-color, #000020)",
      },
    }),
    legendBatter2Label: TextParticleSpec.generateSchema("Legend Label 2", "Legend text for batter 2.", {
      defaultValues: {
        text: "Batter 2",
        family: "Inter, sans-serif",
        size: "0.6875rem",
        weight: "400",
        color: "var(--theme-mode-foreground-color, #000020)",
      },
    }),
    inningsTabText: TextParticleSpec.generateSchema("Innings Tab Text", "Text style for innings tabs.", {
      defaultValues: {
        text: "Innings",
        family: "Inter, sans-serif",
        size: "0.6875rem",
        weight: "600",
        color: "var(--partnership-graph-tab-text-color, var(--theme-mode-foreground-color, #737585))",
        transform: "uppercase",
      },
    }),
    inningsHeadingText: TextParticleSpec.generateSchema(
      "Innings Heading Text",
      "Text style for innings heading labels.",
      {
        defaultValues: {
          text: "Innings",
          family: "Inter, sans-serif",
          size: "0.625rem",
          weight: "700",
          transform: "uppercase",
          color: "var(--theme-mode-foreground-color, #000020)",
        },
      }
    ),
    rowLabelText: TextParticleSpec.generateSchema("Row Label Text", "Text style for partnership row labels.", {
      defaultValues: {
        family: "Inter, sans-serif",
        size: "0.75rem",
        weight: "400",
        align: "right",
        color: "var(--theme-mode-foreground-color, #000020)",
      },
    }),
    runsValueText: TextParticleSpec.generateSchema("Runs Value Text", "Text style for partnership runs values.", {
      defaultValues: {
        family: "Inter, sans-serif",
        size: "0.75rem",
        weight: "700",
        color: "var(--theme-mode-foreground-color, #000020)",
      },
    }),
    ballsValueText: TextParticleSpec.generateSchema("Balls Value Text", "Text style for partnership balls values.", {
      defaultValues: {
        family: "Inter, sans-serif",
        size: "0.625rem",
        weight: "400",
        color: "var(--theme-mode-highlight-color, #737585)",
      },
    }),
    tooltipLineText: TextParticleSpec.generateSchema(
      "Tooltip Line Text",
      "Text style for batter details shown when a bar is hovered/tapped.",
      {
        defaultValues: {
          family: "Inter, sans-serif",
          size: "0.625rem",
          weight: "600",
          color: "var(--theme-mode-foreground-color, #000020)",
        },
      }
    ),
    showInningsHeadings: generateBooleanSchema(
      "Show Innings Headings?",
      "Display innings heading labels when multiple innings are present.",
      {
        defaultValue: true,
      }
    ),
    partnershipInnings: generateArraySchema(
      "Partnership Innings",
      "Normalized innings data for partnership rows.",
      inningsSchema
    ),
    colorBackground: generateColorSchema("Background Color", "Card background color."),
    colorBorder: generateColorSchema("Border Color", "Card border color."),
    colorLegendSwatchBatter1: generateColorSchema("Legend Swatch 1", "Legend swatch color for batter 1."),
    colorLegendSwatchBatter2: generateColorSchema("Legend Swatch 2", "Legend swatch color for batter 2."),
    colorBarBatter1: generateColorSchema("Batter 1 Bar Color", "Bar segment color for batter 1."),
    colorBarBatter2: generateColorSchema("Batter 2 Bar Color", "Bar segment color for batter 2."),
    colorBarExtras: generateColorSchema("Extras Bar Color", "Bar background color for extras."),
    colorTabsBackground: generateColorSchema("Innings Tab Background", "Background color for innings tabs."),
    colorTabsBorder: generateColorSchema("Innings Tab Border", "Border color for innings tabs."),
    colorTabsActiveBackground: generateColorSchema(
      "Innings Active Tab Background",
      "Background color for selected innings tab."
    ),
    colorTabsActiveText: generateColorSchema("Innings Active Tab Text", "Text color for selected innings tab."),
    colorTooltipBackground: generateColorSchema("Tooltip Background", "Tooltip background color."),
    colorTooltipBorder: generateColorSchema("Tooltip Border", "Tooltip border color."),
    barHeight: generateStringSchema("Bar Height", "Height of each partnership bar.", {
      defaultValue: "2.5rem",
    }),
    barCornerRadius: generateStringSchema("Bar Corner Radius", "Corner radius for partnership bars.", {
      defaultValue: "0.125rem",
    }),
    labelWidth: generateStringSchema("Label Width", "Width of left partnership label block.", {
      defaultValue: "6.875rem",
    }),
    runBlockDesktopWidth: generateStringSchema(
      "Runs Block Width (Desktop)",
      "Width of right runs block on desktop.",
      {
        defaultValue: "3.5rem",
      }
    ),
    runBlockMobileWidth: generateStringSchema("Runs Block Width (Mobile)", "Width of right runs block on mobile.", {
      defaultValue: "2rem",
    }),
  })
);
